import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DraggingStyle, NotDraggingStyle } from "react-beautiful-dnd";
import { Badge, Form, Modal } from "react-bootstrap";
import { PipelineNode, PipelineNodeField, PipelineNodeMapOption } from "@models/pipelineNode";
import { DraftOnly, ReadOnly } from "@components/project/DraftModeRequired.component";
import { UnlockedFieldRequired } from "@components/project/UnlockedRequired.component";
import EditableText from "@components/general/EditableText.component";
import PipelineNodeColumnCleanerUpper, { PipelineNodeFieldCleaningBadges } from "./configuration/PipelineNodeColumnCleanerUpper.component";
import PipelineNodeDragAndDropMapping from "./mapping/PipelineNodeDragAndDropMapping.component";
import BusinessObjectFieldTypeSelector from "@components/businessObjects/BusinessObjectFieldTypeSelector.component";
import { requireConfirmation } from "@services/alert/alert.service";
import { SYSTEM_FIELD_TYPES } from "@services/modeling.service";
import { useIsInDraftMode, usePipelineNodes } from '@stores/data.store';
import PipelineNodeFieldMap from './mapping/PipelineNodeFieldMap.component';
import { useDebounce } from 'use-debounce';
import { shortid } from '@services/id.service';
import FullScreenTakeover from "@components/general/FullScreenTakeover.component";
import PipelineNodeColumnLineage from "./PipelineNodeColumnLineage.component";
import Dropdown from "@components/form/Dropdown.component";
import { getAggregationOptionsForFieldType } from "@services/metrics.service";

const reorder = (list: any[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
  
    return result;
};

interface PipelineNodeFieldsTableProps {
    node: PipelineNode;
    onChange: (updater: (draft: PipelineNode) => void) => void;
    disableAddColumn?: boolean;
    disableRemoveColumn?: boolean;
    hideFormatting?: boolean;
    disableRenameColumn?: boolean;
}

const PipelineNodeFieldsTable: React.FC<PipelineNodeFieldsTableProps> = ({
    node,
    onChange,
    disableAddColumn = false,
    disableRemoveColumn = false,
    hideFormatting = false,
    disableRenameColumn = false,
}) => {
    const pipelineNodes = usePipelineNodes();

    // Internal state for modals and interactions
    const [activeFieldId, setActiveFieldId] = useState('');
    const [showColumnEditor, setShowColumnEditor] = useState(false);
    const [activeColumnForFieldMapping, setActiveColumnForFieldMapping] = useState<PipelineNodeField|undefined>(undefined);
    const [activeColumnMapOptions, setActiveColumnMapOptions] = useState<PipelineNodeMapOption[]>([]);
    const [activeColumnFormula, setActiveColumnFormula] = useState('');
    const [activeColumnAdvancedMode, setActiveColumnAdvancedMode] = useState(false);
    const [showMergeBehaviorModal, setShowMergeBehaviorModal] = useState(false);
    const [activeMergeBehaviorField, setActiveMergeBehaviorField] = useState<PipelineNodeField|undefined>(undefined);
    const [activeMergeBehaviorFieldDatatype, setActiveMergeBehaviorFieldDatatype] = useState<string>('');
    const [activeMergeBehaviorFieldAggregator, setActiveMergeBehaviorFieldAggregator] = useState<string>('');
    const [showColumnLineage, setShowColumnLineage] = useState(false);
    const [activeColumnForLineage, setActiveColumnForLineage] = useState<PipelineNodeField|undefined>(undefined);

    const isSummarize = node.node_type == 'SUMMARIZE' || node.grouping_behavior == 'SUMMARIZE';

    const allFields = useMemo(() => {
        return node.fields.filter(f => !SYSTEM_FIELD_TYPES.includes(f.type) && f.label.toLowerCase());
    }, [node.fields]);

    const columnWidths = useMemo(() => {
        if (['DIMENSION', 'DATE_DIMENSION'].includes(node.node_type)) {
            return ['5%', '5%', '35%', '20%', '25%', '10%'];
        }
        if (isSummarize) {
            return ['5%', '0%', '20%', '20%', '20%', '20%', '10%'];
        }
        return ['5%', '0%', '35%', '20%', '25%', '10%'];
    }, [node.node_type, isSummarize]);


    const customFormulaRef = useRef<HTMLTextAreaElement>(null);

    // Get upstream nodes for field mapping
    const upstreamNodes = useMemo(() => {
        if (!pipelineNodes.data) {
            return [];
        }
        return node.upstream_node_ids.map(id => pipelineNodes.data.find(pn => pn.id === id)).filter(Boolean) as PipelineNode[];   
    }, [node.upstream_node_ids, pipelineNodes.data]);

    // Get active field for column editor
    const activeField = useMemo(() => {
        if (activeFieldId) {
            return node.fields.find(f => f.id === activeFieldId);
        }
        return undefined;
    }, [activeFieldId, node.fields]);

    // Internal functions
    const onColumnReorder = useCallback((result: any) => {
        if (!result.destination) {
            return;
        }
        onChange(draft => {
            const unmanagedFields = draft.fields.filter(f => SYSTEM_FIELD_TYPES.includes(f.type));
            const managedFields = draft.fields.filter(f => !SYSTEM_FIELD_TYPES.includes(f.type));
            const newFields = reorder(managedFields, result.source.index, result.destination.index);
            draft.fields = newFields.concat(unmanagedFields);
        });
    }, [onChange]);

    const toggleFieldDimension = useCallback((fieldId: string) => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === fieldId);
            if (theField) {
                theField.is_dimension = !theField.is_dimension;
            }
        });
    }, [onChange]);

    const updateColumnLabel = useCallback((fieldId: string, newLabel: string) => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === fieldId);
            if (theField) {
                theField.label = newLabel;
            }
        });
    }, [onChange]);

    const updateColumnDescription = useCallback((fieldId: string, newDescription: string) => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === fieldId);
            if (theField) {
                theField.description = newDescription;
            }
        });
    }, [onChange]);

    const onDeleteField = useCallback(async (field: PipelineNodeField) => {
        const confirmed = await requireConfirmation('Are you sure you want to delete this column?', 'Delete Column');
        if (!confirmed) {
            return;
        }
        onChange(draft => {
            draft.fields = draft.fields.filter(f => f.id !== field.id);
        });
    }, [onChange]);

    const editFieldMergeBehavior = useCallback((field: PipelineNodeField) => {
        setActiveMergeBehaviorField(field);
        setActiveMergeBehaviorFieldDatatype(field.type);
        setActiveMergeBehaviorFieldAggregator(field.merge_behavior || 'PICK_ONE');
        setShowMergeBehaviorModal(true);
    }, []);

    const showColumnLineageForField = useCallback((field: PipelineNodeField) => {
        setActiveColumnForLineage(field);
        setShowColumnLineage(true);
    }, []);

    const updateMapOptionsForActiveColumn = useCallback((newOptions: PipelineNodeMapOption[]) => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === activeColumnForFieldMapping?.id);
            if (theField) {
                theField.map_options = newOptions;
            }
        });    
    }, [activeColumnForFieldMapping, onChange]);

    const updateActiveColumnFormula = useCallback((newFormula: string) => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === activeColumnForFieldMapping?.id);
            if (theField) {
                theField.custom_sql = newFormula;
                theField.advanced_mode = activeColumnAdvancedMode;
            }
        });
    }, [activeColumnForFieldMapping, activeColumnAdvancedMode, onChange]);

    const onUpdateActiveField = useCallback((updatedField: PipelineNodeField) => {
        onChange(draft => {
            const idx = draft.fields.findIndex(f => f.id === updatedField.id);
            if (idx >= 0) {
                draft.fields[idx] = updatedField;
            }
        });
        setActiveFieldId('');
        setShowColumnEditor(false);
    }, [onChange]);

    const onUpdateMergeBehavior = useCallback(() => {
        if (!activeMergeBehaviorField) return;
        
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === activeMergeBehaviorField.id);
            if (theField) {
                theField.type = activeMergeBehaviorFieldDatatype;
                theField.merge_behavior = activeMergeBehaviorFieldAggregator;
            }
        });
        setShowMergeBehaviorModal(false);
    }, [activeMergeBehaviorField, activeMergeBehaviorFieldDatatype, activeMergeBehaviorFieldAggregator, onChange]);

    const saveMappingColumn = useCallback(() => {
        onChange(draft => {
            const theField = draft.fields.find(f => f.id === activeColumnForFieldMapping?.id);
            if (theField) {
                
                theField.map_options = activeColumnMapOptions;
                theField.advanced_mode = activeColumnAdvancedMode;
                theField.custom_sql = customFormulaRef.current?.value || '';
            }
        });    
        setActiveColumnForFieldMapping(undefined);
    }, [onChange, activeColumnMapOptions, customFormulaRef, activeColumnAdvancedMode, activeColumnForFieldMapping]);

    // Effects
    useEffect(() => {
        if (!!activeFieldId) {
            setShowColumnEditor(true);
        }
    }, [activeFieldId]);

    useEffect(() => {
        if (activeColumnForFieldMapping) {
            setActiveColumnMapOptions(activeColumnForFieldMapping.map_options || []);
            setActiveColumnFormula(activeColumnForFieldMapping.custom_sql || '');
            setActiveColumnAdvancedMode(activeColumnForFieldMapping.advanced_mode || false);
        }
    }, [activeColumnForFieldMapping]);
    const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle | undefined) => ({
        userSelect: "none" as const,
        display: isDragging ? 'table' : 'table-row',
        borderTop: isDragging ? '1px solid var(--ct-border-color)' : 'none',
        backgroundColor: isDragging ? 'white' : 'inherit',
        borderLeft: isDragging ? '1px solid var(--ct-border-color)' : 'none',
        borderRight: isDragging ? '1px solid var(--ct-border-color)' : 'none',
        ...draggableStyle
    });

    const [boSearchInput, setBoSearchInput] = useState('');
    const [boSearchTerm] = useDebounce(boSearchInput, 250);

    const inDraftMode = useIsInDraftMode();
    const filteredFields = useMemo(() => {
        return allFields.filter(f => f.label.toLowerCase().indexOf(boSearchTerm) >= 0);
    }, [allFields, boSearchTerm]);

    const isDraggingDisabled = useMemo(() => {
        return !inDraftMode || !!boSearchTerm;
    }, [boSearchTerm, inDraftMode]);

    const addNewFieldAtTop = useCallback(() => {
        const newId = shortid();
        onChange(draft => {
            draft.fields.unshift({
                id: newId,
                name: '',
                description: '',
                part_of_composite_key: false,
                label: 'New Field', 
                type: 'STRING',
                map_options: [],
                taxonomic_id: '',
                cell_actions: [],
            })
        });
    }, [onChange]);

    return <div>
        <div className="mb-3">
            <div className="d-flex center-vertically mb-1 justify-content-between">            
                <h2 className="mb-0"><i className="mdi mdi-cog"></i> Configure Columns</h2> 
                <div className="pe-5" style={{width: '40%'}}>
                    <input type="text" className="search-input" value={boSearchInput} onChange={(e) => setBoSearchInput(e.target.value)} placeholder="Search columns"/>
                </div>
                {!disableAddColumn && <DraftOnly>
                    <button className="btn btn-outline-primary btn-sm" onClick={() => {
                        addNewFieldAtTop();
                    }}>
                        <i className="mdi mdi-plus-circle"></i> Add Column
                    </button>
                </DraftOnly>}
                
            </div>
        </div>
        <DragDropContext onDragEnd={onColumnReorder}>
            <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                    <table
                        ref={provided.innerRef}
                        className="table table-centered table-fixed w-100"
                    > 
                        <thead className="bg-light">
                            <tr>
                                <th style={{width: columnWidths[0]}}></th>
                                {['DATE_DIMENSION', 'DIMENSION'].includes(node.node_type) && <th style={{width: columnWidths[1]}}></th>}
                                
                                <th style={{width: columnWidths[2]}}>Name</th>

                                {!hideFormatting && <th style={{width: columnWidths[3]}}>Data</th>}
                                {!hideFormatting && <th style={{width: columnWidths[4]}}>Formatting</th>}
                                {isSummarize && <>
                                    <th style={{width: columnWidths[5]}}>Aggregation</th>
                                    <th style={{width: columnWidths[6]}}></th>
                                </>}
                                {!isSummarize && <>
                                    <th style={{width: columnWidths[5]}}></th>
                                </>}
                            </tr>
                        </thead>
                        <tbody>
                        {filteredFields.map((f, idx) => {
                            return <Draggable key={f.id} draggableId={f.id} index={idx} isDragDisabled={isDraggingDisabled}>
                                
                                {(draggableProvided, snapshot) => {
                                    if (draggableProvided.draggableProps.style) {
                                        var transform = draggableProvided.draggableProps.style?.transform
                                        if(transform){
                                            var t = transform.split(",")[1]
                                            draggableProvided.draggableProps.style.transform = "translate(0px," + t
                                        }
                                    }
                                    
                                    return <tr 
                                        ref={draggableProvided.innerRef} 
                                        {...draggableProvided.draggableProps} 
                                        // @ts-ignore
                                        style={getItemStyle(
                                            snapshot.isDragging,
                                            draggableProvided.draggableProps.style
                                        )}
                                    >
                                        <td style={{width: columnWidths[0]}}>
                                            {!isDraggingDisabled && <>
                                                <div {...draggableProvided.dragHandleProps} className="font-24">
                                                    <i className="mdi mdi-drag"></i>
                                                </div>
                                            </>}
                                            
                                        </td>
                                        {['DATE_DIMENSION', 'DIMENSION'].includes(node.node_type) && <td style={{width: columnWidths[1]}}>
                                            <DraftOnly>
                                                <button className={`icon-button font-24 ${f.is_dimension ? 'text-pliable' : ''}`} title="Toggle dimension" onClick={() => {
                                                    toggleFieldDimension(f.id);
                                                }}>
                                                    <i className="mdi mdi-label"></i>
                                                </button>
                                            </DraftOnly>
                                            
                                        </td>}
                                        <td style={{width: columnWidths[2]}}>
                                            <div className="d-flex center-vertically">
                                                <ReadOnly>
                                                    <div>
                                                        <strong>
                                                            {disableRenameColumn ? (
                                                                f.label
                                                            ) : (
                                                                <EditableText
                                                                    value={f.label}
                                                                    onChange={(newVal) => {
                                                                        updateColumnLabel(f.id, newVal);
                                                                    }}
                                                                />
                                                            )}
                                                        </strong>
                                                        <div className="small">
                                                            <EditableText
                                                                value={f.description}
                                                                onChange={(newVal) => {
                                                                    updateColumnDescription(f.id, newVal);
                                                                }}
                                                                emptyPlaceholder="No Description"
                                                            />
                                                        </div>
                                                    </div>
                                                </ReadOnly>
                                            </div>
                                        </td>
                                        {!hideFormatting && <td style={{width: columnWidths[3]}}>
                            
                                                <div className="clickable" onClick={() => {
                                                    setActiveColumnForFieldMapping(f);
                                                    setActiveColumnMapOptions(f.map_options);
                                                    setActiveColumnFormula(f.custom_sql || '');
                                                    setActiveColumnAdvancedMode(f.advanced_mode || false);
                                                }}>
                                                    {f.part_of_composite_key && (!f.map_options || (f.map_options.length != node.upstream_node_ids.length && node.combine_logic_gate == 'AND')) && <>
                                                        <Badge bg="danger" className="me-1">Missing mappings</Badge>
                                                    </>}
                                                    {!f.map_options.length && !f.advanced_mode && !f.part_of_composite_key && (
                                                        <Badge bg="warning">No mapped columns</Badge>
                                                    )}
                                                    {!f.advanced_mode && f.map_options.length >= 1 && (
                                                        <Badge bg="info">{f.map_options.length} mapped column{f.map_options.length > 1 ? 's' : ''}</Badge>
                                                    )}
                                                    {f.advanced_mode && (
                                                        <Badge bg="dark"><i className="mdi mdi-code-braces"></i> Calculated</Badge>
                                                    )}
                                                </div>
                                   
                                        </td>}
                                        {!hideFormatting && <td style={{width: columnWidths[4]}}>
                                            <PipelineNodeFieldCleaningBadges
                                                datatype={f.type}
                                                transform={f.advanced_mode ? '' : f.transformer}
                                                onEdit={() => {
                                                    setActiveFieldId(f.id);
                                                }}
                                            />
                                        </td>}
                                        
                                        {isSummarize && <>
                                            <td style={{width: columnWidths[5]}}>
                                                {f.part_of_composite_key && (
                                                    <Badge pill bg="pliable">Group By</Badge>
                                                )}
                                                {!f.part_of_composite_key && (
                                                    <Badge className="clickable" pill bg="primary" onClick={() => {
                                                        editFieldMergeBehavior(f);
                                                    }}>{f.merge_behavior || 'PICK_ONE'}</Badge>
                                                )}
                                            </td>
                                            <td className="text-end font-18" style={{width: columnWidths[6]}}>
                                                <div className="d-flex justify-content-end gap-1">
                                                    <button onClick={(e) => {
                                                        e.stopPropagation();
                                                        showColumnLineageForField(f);
                                                    }} className="icon-button" title="View Column Lineage">
                                                        <i className="mdi mdi-graph"></i>
                                                    </button>
                                                    {!disableRemoveColumn && (
                                                        <DraftOnly>
                                                            <UnlockedFieldRequired pipelineNodeFields={[f]}>
                                                                <button onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDeleteField(f)
                                                                }} className="icon-button">
                                                                    <i className="mdi mdi-delete"></i>
                                                                </button>
                                                            </UnlockedFieldRequired>
                                                        </DraftOnly>
                                                    )}
                                                </div>
                                            </td>
                                        </>}
                                        {!isSummarize && <>
                                            <td className="text-end font-18" style={{width: columnWidths[5]}}>
                                                <div className="d-flex justify-content-end gap-1">
                                                    <button onClick={(e) => {
                                                        e.stopPropagation();
                                                        showColumnLineageForField(f);
                                                    }} className="icon-button" title="View Column Lineage">
                                                        <i className="mdi mdi-graph"></i>
                                                    </button>
                                                    {!disableRemoveColumn && (
                                                        <DraftOnly>
                                                            <UnlockedFieldRequired pipelineNodeFields={[f]}>
                                                                <button onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDeleteField(f)
                                                                }} className="icon-button">
                                                                    <i className="mdi mdi-delete"></i>
                                                                </button>
                                                            </UnlockedFieldRequired>
                                                        </DraftOnly>
                                                    )}
                                                </div>
                                            </td>
                                        </>}
                                        
                                    </tr>
                                    }}
                            </Draggable>
                        })}
                        {provided.placeholder}
                        </tbody>
                        
                    </table>
                )}
            </Droppable>
        </DragDropContext>

        {/* Column Editor Modal */}
        <PipelineNodeColumnCleanerUpper
            field={activeField}
            isCalculated={activeField?.advanced_mode}
            show={showColumnEditor}
            onChange={onUpdateActiveField}
            onClose={() => {
                setActiveFieldId('');
                setShowColumnEditor(false);
            }}
            sourcePipelineNodeId={activeField?.map_options && activeField.map_options.length == 1 && activeField.map_options[0].source_node_id ? activeField.map_options[0].source_node_id : node.id as string}
            sourceFieldId={activeField?.map_options && activeField.map_options.length == 1 && activeField.map_options[0].attribute_id ? activeField.map_options[0].attribute_id : (activeField?.id || '')}
        />

        {/* Merge Behavior Modal */}
        <Modal show={!!showMergeBehaviorModal} onHide={() => setShowMergeBehaviorModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Edit Aggregation: {activeMergeBehaviorField?.label}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {activeMergeBehaviorField && <>
                    <Form.Group className="mb-3">
                        <Form.Label>Datatype</Form.Label>
                        <BusinessObjectFieldTypeSelector
                            selected={activeMergeBehaviorFieldDatatype || ''}
                            onSelect={setActiveMergeBehaviorFieldDatatype}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Aggregation</Form.Label>
                        <Dropdown
                            selected={activeMergeBehaviorFieldAggregator}
                            onChange={setActiveMergeBehaviorFieldAggregator}
                            options={getAggregationOptionsForFieldType(activeMergeBehaviorFieldDatatype)}
                        />
                    </Form.Group>
                </>}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={() => setShowMergeBehaviorModal(false)}>
                    Cancel
                </button>
                <button className="btn btn-primary" onClick={onUpdateMergeBehavior}>
                    Save
                </button>
            </Modal.Footer>
        </Modal>

        {/* Field Mapping Modal */}
        <Modal show={!!activeColumnForFieldMapping} onHide={() => setActiveColumnForFieldMapping(undefined)}>
            <Modal.Header closeButton>
                <Modal.Title>Column Data: {activeColumnForFieldMapping?.label}</Modal.Title>

            </Modal.Header>
            <Modal.Body>
                <ReadOnly>
                    {activeColumnForFieldMapping && <>
                        <div className="fw-bold">
                            <Form.Check
                                type="switch"
                                label="Calculated Column"
                                checked={activeColumnAdvancedMode}
                                onChange={(e) => setActiveColumnAdvancedMode(e.target.checked)}
                            />
                        </div>
                        
                        <Form.Text>
                            Calculated columns allow you to write formulas based on the values in other columns. This can be useful for combining columns, performing calculations, or creating new columns based on existing data.
                        </Form.Text>
                        <hr />
                        {!activeColumnAdvancedMode && <>


                            <PipelineNodeFieldMap
                                showPriority={false}
                                mapOptions={activeColumnMapOptions}
                                onChangeMapOptions={setActiveColumnMapOptions}
                                displayJoinPath={false} 
                                isUnlocked={true}
                                sourceNodes={upstreamNodes}

                            />
                        </>}
                        {activeColumnAdvancedMode && <>
                            <Form.Label>Formula</Form.Label>
                            <Form.Control className="font-code text-13" ref={customFormulaRef} defaultValue={activeColumnFormula} as="textarea"  />
                            <Form.Text>Write a custom formula in Snowflake SQL. You can use any value that is mapped to this node by wrapping the column name in double quotes.</Form.Text>
                                    
                        </>}
                    </>}
                </ReadOnly>
            </Modal.Body>
            <Modal.Footer>
                <ReadOnly>
                    <button className="btn btn-success" onClick={saveMappingColumn}>
                        Save
                    </button>
                </ReadOnly>
            </Modal.Footer>
        </Modal>

        {/* Column Lineage FullScreenTakeover */}
        <FullScreenTakeover show={showColumnLineage} onHide={() => {
            setShowColumnLineage(false);
            setActiveColumnForLineage(undefined);
        }}>
            {activeColumnForLineage && node.id && (
                <div className="p-3 d-flex flex-column gap-3 h-100">
                    <h2>Column Lineage: {node.label} - {activeColumnForLineage.label}</h2>
                    <div className="flex-1 rounded border">
                        
                    <PipelineNodeColumnLineage 
                    pipelineNodeId={node.id} 
                    fieldId={activeColumnForLineage.id} 
                />
                </div>
                </div>
                
                
            )}
        </FullScreenTakeover>
    </div>;
};

export default PipelineNodeFieldsTable;
